/*
 * Geo Store Switcher — Country/Market Suggestion Banner
 * --------------------------------------------------------------------------
 * Single-store Shopify Markets implementation.
 *
 * Detects the visitor's country via Shopify's native
 * `/browsing_context_suggestions.json` endpoint and, when it differs from the
 * market currently being browsed, shows a dismissible suggestion banner.
 * Accepting the suggestion submits Shopify's native localization form
 * (`country_code` + `locale_code` + `return_to`) so the user stays on the same
 * page in the new market/locale.
 *
 * No metafields and no custom hreflang layer are needed: Shopify Markets
 * preserves page context and emits the correct alternates natively.
 */
(function () {
  'use strict';

  var SUGGESTIONS_ENDPOINT = '/browsing_context_suggestions.json';

  var COOKIE = {
    pref: 'ahead_market_pref',
    prefSource: 'ahead_market_pref_source',
    event: 'ahead_geo_event',
  };

  var STORAGE = {
    dismissed: 'ahead_geo_banner_dismissed',
  };

  var DAY = 24 * 60 * 60 * 1000;

  /* ----------------------------------------------------------------------- */
  /* Cookie helpers                                                          */
  /* ----------------------------------------------------------------------- */

  function setCookie(name, value, ttlMs) {
    var expires = new Date(Date.now() + ttlMs).toUTCString();
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; expires=' +
      expires +
      '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  function deleteCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  }

  /* ----------------------------------------------------------------------- */
  /* Storage helpers (fail-safe for private mode)                            */
  /* ----------------------------------------------------------------------- */

  function getStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function setStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* ignore */
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Analytics                                                               */
  /* ----------------------------------------------------------------------- */

  function pushEvent(event, detail) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: event };
    if (detail) {
      for (var key in detail) {
        if (Object.prototype.hasOwnProperty.call(detail, key)) {
          payload[key] = detail[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  /* Queue an event across the localization form reload. */
  function queueEvent(event, detail) {
    try {
      setCookie(
        COOKIE.event,
        JSON.stringify({ event: event, detail: detail || {} }),
        DAY
      );
    } catch (e) {
      /* ignore */
    }
  }

  /* Replay any event that was queued before a market switch reload. */
  function consumeQueuedEvent() {
    var raw = getCookie(COOKIE.event);
    if (!raw) return;
    deleteCookie(COOKIE.event);
    try {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.event) {
        pushEvent(parsed.event, parsed.detail);
      }
    } catch (e) {
      /* ignore malformed cookie */
    }
  }

  /* ----------------------------------------------------------------------- */
  /* GeoStoreSwitcher                                                        */
  /* ----------------------------------------------------------------------- */

  function GeoStoreSwitcher(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.form = root.querySelector('[data-geo-form]') || root.querySelector('form');
    this.select = root.querySelector('[data-geo-select]');
    this.countryInput = root.querySelector('[data-geo-country-input]');
    this.localeInput = root.querySelector('[data-geo-locale-input]');
    this.returnInput = root.querySelector('[data-geo-return-input]');
    this.continueBtn = root.querySelector('[data-geo-continue]');
    this.closeBtn = root.querySelector('[data-geo-close]');
    this.locationName = root.querySelector('[data-geo-location-name]');

    this.currentCountry = (this.config.currentCountry || '').toUpperCase();
    this.currentLocale = (this.config.currentLocale || '').toLowerCase();

    this.bindEvents();
    this.init();
  }

  GeoStoreSwitcher.prototype.readConfig = function () {
    var node = this.root.querySelector('[data-geo-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  };

  GeoStoreSwitcher.prototype.bindEvents = function () {
    var self = this;

    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.switchMarket();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.dismiss();
      });
    }
  };

  GeoStoreSwitcher.prototype.init = function () {
    // In the theme editor we can force-render for styling without detection.
    if (this.config.showInEditor && this.config.inEditor) {
      this.show(this.currentCountry, { skipAnalytics: true });
      return;
    }

    if (this.isDismissed()) {
      return;
    }

    // A manual preference always wins over geolocation detection.
    var pref = this.getManualPreference();
    if (pref && pref !== this.currentCountry) {
      this.show(pref);
      return;
    }
    if (pref && pref === this.currentCountry) {
      // The user is already where they chose to be — never nag.
      return;
    }

    this.scheduleDetect();
  };

  GeoStoreSwitcher.prototype.getManualPreference = function () {
    var source = getCookie(COOKIE.prefSource);
    if (source !== 'manual') return null;
    var pref = getCookie(COOKIE.pref);
    return pref ? pref.toUpperCase() : null;
  };

  GeoStoreSwitcher.prototype.isDismissed = function () {
    return getStorage(STORAGE.dismissed) === '1';
  };

  GeoStoreSwitcher.prototype.scheduleDetect = function () {
    var self = this;
    var run = function () {
      self.detectAndCompare();
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 3000 });
      return;
    }
    if (document.readyState === 'complete') {
      setTimeout(run, 1);
    } else {
      window.addEventListener('load', function () {
        setTimeout(run, 1);
      });
    }
  };

  GeoStoreSwitcher.prototype.detectAndCompare = function () {
    var self = this;
    fetch(SUGGESTIONS_ENDPOINT, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('detection_failed');
        return res.json();
      })
      .then(function (data) {
        var detected = self.extractCountry(data);
        if (!detected) {
          self.handleFallback('no_country');
          return;
        }

        if (!self.canShowForDetectedCountry(detected)) {
          return;
        }

        // Detected country matches the current market — nothing to suggest.
        if (detected === self.currentCountry) {
          return;
        }

        if (self.isAvailableCountry(detected)) {
          self.show(detected);
        } else {
          // Country isn't sold here — suggest the configured fallback.
          var fallback = (self.config.defaultCountry || '').toUpperCase();
          self.handleFallback('unsupported_country', detected);
          if (fallback && fallback !== self.currentCountry && self.isAvailableCountry(fallback)) {
            self.show(fallback);
          }
        }
      })
      .catch(function () {
        self.handleFallback('detection_error');
      });
  };

  GeoStoreSwitcher.prototype.extractCountry = function (data) {
    if (!data || !data.detected_values || !data.detected_values.country) {
      return null;
    }
    var country = data.detected_values.country;
    var code = country.iso_code || country.handle;
    return code ? String(code).toUpperCase() : null;
  };

  GeoStoreSwitcher.prototype.isAvailableCountry = function (iso) {
    var countries = this.config.availableCountries || [];
    for (var i = 0; i < countries.length; i++) {
      if ((countries[i].iso || '').toUpperCase() === iso) {
        return true;
      }
    }
    return false;
  };

  GeoStoreSwitcher.prototype.canShowForDetectedCountry = function (iso) {
    if (!this.config.restrictBannerCountries) {
      return true;
    }

    var countries = this.config.bannerCountryFilter || [];
    for (var i = 0; i < countries.length; i++) {
      if (String(countries[i] || '').toUpperCase() === iso) {
        return true;
      }
    }

    return false;
  };

  GeoStoreSwitcher.prototype.resolveLocale = function (iso) {
    var map = this.config.countryLocaleMap || {};
    var available = this.config.availableLocales || [];
    var locale = map[iso];
    // Only switch the locale if it is actually published.
    if (locale && available.indexOf(locale) !== -1) {
      return locale;
    }
    return this.currentLocale;
  };

  GeoStoreSwitcher.prototype.show = function (iso, options) {
    options = options || {};

    if (this.select) {
      this.select.value = iso;
      // If the detected country isn't a select option, fall back to current.
      if (this.select.value !== iso) {
        this.select.value = this.currentCountry;
      }
    }

    if (this.locationName) {
      this.locationName.textContent = this.countryName(this.select ? this.select.value : iso);
    }

    this.root.hidden = false;

    if (!options.skipAnalytics) {
      pushEvent('geo_banner_shown', {
        detected_country: iso,
        current_country: this.currentCountry,
      });
    }
  };

  GeoStoreSwitcher.prototype.countryName = function (iso) {
    var countries = this.config.availableCountries || [];
    for (var i = 0; i < countries.length; i++) {
      if ((countries[i].iso || '').toUpperCase() === iso) {
        return countries[i].name || iso;
      }
    }
    return iso;
  };

  GeoStoreSwitcher.prototype.switchMarket = function () {
    var iso = (this.select ? this.select.value : this.currentCountry).toUpperCase();
    var locale = this.resolveLocale(iso);
    // "locale": change language only (keep current market, URL like /fr).
    // "market_locale": change market + language (URL like /fr-fr).
    var localeOnly = this.config.switchMode === 'locale';

    if (this.countryInput) {
      if (localeOnly) {
        // Don't submit a country change so the current market is preserved,
        // matching the native language selector behaviour.
        this.countryInput.disabled = true;
      } else {
        this.countryInput.disabled = false;
        this.countryInput.value = iso;
      }
    }

    if (this.localeInput) this.localeInput.value = locale;
    if (this.returnInput) {
      this.returnInput.value = window.location.pathname + window.location.search;
    }

    // Persist the manual choice so detection never overrides it again.
    setCookie(COOKIE.pref, iso, 30 * DAY);
    setCookie(COOKIE.prefSource, 'manual', 30 * DAY);

    // Queue the switch event so it survives the form-triggered reload.
    queueEvent('geo_store_switch', {
      from_country: this.currentCountry,
      to_country: localeOnly ? this.currentCountry : iso,
      from_locale: this.currentLocale,
      to_locale: locale,
      switch_mode: localeOnly ? 'locale' : 'market_locale',
    });

    if (this.form) {
      this.form.submit();
    }
  };

  GeoStoreSwitcher.prototype.dismiss = function () {
    setStorage(STORAGE.dismissed, '1');
    this.root.hidden = true;
  };

  GeoStoreSwitcher.prototype.handleFallback = function (reason, detected) {
    pushEvent('geo_fallback', {
      reason: reason,
      detected_country: detected || null,
      current_country: this.currentCountry,
    });
  };

  /* ----------------------------------------------------------------------- */
  /* Bootstrap                                                               */
  /* ----------------------------------------------------------------------- */

  function bootstrap() {
    // Replay any analytics event queued before a market-switch reload.
    consumeQueuedEvent();

    var roots = document.querySelectorAll('[data-geo-store-switcher]');
    for (var i = 0; i < roots.length; i++) {
      new GeoStoreSwitcher(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
